import React from 'react';
import MainLayout from '../../../mainLayout';
import styled from 'styled-components/native';
import {useTheme} from 'styled-components';
import {RefreshControl} from 'react-native';
import {Theme} from '../../../../theme/lightTheme';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import screens from '../../../../config/screens';
import {tr} from '../../../../translations';
import {RootState} from '../../../../redux/store';
import {useSelector} from 'react-redux';
import {getHomeTabs} from '../actions';
import HomeTabsSkeleton from '../../../../components/homeTabsSkeleton';
import {ScreenProps} from '../../../../interfaces/screens/mainScreen';
import EmptyList from '../../../../components/emptyList';
import {NavigationProp, useNavigation} from '@react-navigation/native';
import Animated, {FadeInDown, FadeOut} from 'react-native-reanimated';
import HomeTab from '../../../../components/homeTab';
import {Platform} from 'react-native';
import '../../../../utils/webAnimations';
import {
  homePopularTopicsTabs,
  tabImages,
} from '../../../../assets/static/homeTabs';
import {HomeTabItemType} from '../../../../interfaces/reducers/homeState';
import {EmptyContainer, Space} from '../index';
import useResponsiveDimensions from '../../../../utils/hooks/useResponsiveDimensions';

const Container = styled(Animated.ScrollView)`
  width: 100%;
`;
const ContentContainer = styled.View`
  width: 94%;
  align-self: center;
  z-index: 99;
`;
const PaddingView = styled.View`
  z-index: 99;
  flex-wrap: wrap;
  flex: 1;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-evenly;
`;

/**
 * AllTopics screen component.
 *
 * @param {any} props - The screen component props.
 * @param {object} props.navigation - The navigation object.
 */
const AllTopics: React.FC<ScreenProps> = () => {
  const theme = useTheme() as Theme;
  const navigation = useNavigation<NavigationProp<any>>();
  const {bottom: bottomInset} = useSafeAreaInsets();
  const {tabs, isLoading, isError} = useSelector(
    (state: RootState) => state.homeTabs,
  );
  const homeTabs = (tabs || []).filter(i => i.active !== 0);

  // Add useResponsiveDimensions hook
  const {hdp, getByScreenSize} = useResponsiveDimensions();

  const onTabPressHandler = (item: HomeTabItemType) => {
    if (Array.isArray(item?.items)) {
      navigation?.navigate(screens.content.calculators, {
        data: item?.items,
      });
      return;
    }
    if (item?.has_topics) {
      navigation?.navigate(screens.content.topics, item);
      return;
    }
    if (item?.pdf) {
      navigation?.navigate(screens.content.fileViewer, item);
      return;
    }
    navigation?.navigate(screens.content.filterTabNews, item);
  };

  return (
    <MainLayout
      backHeader
      enableScroll={false}
      showOverlay={false}
      title={tr('home.medicalUpdatesTitle')}
      noPadding>
      <Container
        contentContainerStyle={{
          maxWidth: getByScreenSize('100%', '60%'),
          alignSelf: 'center',
        }}
        refreshControl={
          <RefreshControl
            tintColor={theme.primary}
            refreshing={isLoading && homeTabs?.length === 0}
            onRefresh={getHomeTabs}
          />
        }>
        <ContentContainer>
          {isError ? (
            <EmptyContainer>
              <EmptyList
                iconName={'alert'}
                iconSize={theme.text.s1 * 2.5}
                onRefreshPress={getHomeTabs}
                title={'app.error'}
                showRefresh
                containerStyle={{backgroundColor: theme.cardBackground}}
              />
            </EmptyContainer>
          ) : isLoading ? (
            <HomeTabsSkeleton />
          ) : (
            <>
              <PaddingView style={{zIndex: 2, paddingTop: hdp(3)}}>
                {(homeTabs || []).map((item, index) => {
                  return (
                    <Animated.View
                      key={item.id}
                      style={{
                        width: getByScreenSize('48%', '28%'),
                        marginBottom: '2%',
                      }}
                      {...(Platform.OS === 'web'
                        ? {className: 'screen-fade-in-down'}
                        : {entering: FadeInDown.delay(index * 50), exiting: FadeOut})}>
                      <HomeTab
                        title={item.tab_name || ''}
                        onPress={() => onTabPressHandler(item)}
                        imageUri={item.img_url}
                        defaultImage={
                          item.tab_value
                            ? tabImages[
                                item.tab_value as keyof typeof tabImages
                              ]
                            : homePopularTopicsTabs[
                                index % homePopularTopicsTabs.length
                              ].img
                        }
                      />
                    </Animated.View>
                  );
                })}
                <Space bottomInset={bottomInset} extraSpace={hdp(10)} />
              </PaddingView>
            </>
          )}
        </ContentContainer>
      </Container>
    </MainLayout>
  );
};

export default AllTopics;
